import type { Directive, DirectiveBinding } from 'vue';

// Define interfaces for wheel event handlers
interface ZoomEventData {
  delta: number;
  direction: 'in' | 'out';
  event: WheelEvent;
  mouseX: number;
  mouseY: number;
}

interface ScrollEventData {
  deltaX: number;
  deltaY: number;
  event: WheelEvent;
}

interface MouseWheelHandlers {
  onZoom?: (data: ZoomEventData) => void;
  onScroll?: (data: ScrollEventData) => void;
}

// Extend HTMLElement to include our custom property
interface MouseWheelElement extends HTMLElement {
  _wheelHandler?: (event: WheelEvent) => void;
}

// Format dimension interface
interface FormatDimension {
  width: number;
  height: number;
}

/**
 * Custom Vue directive for advanced mouse wheel handling
 * This allows for both zooming and scrolling in the canvas
 */
export const vMouseWheel: Directive<MouseWheelElement, MouseWheelHandlers> = {
  mounted(el: MouseWheelElement, binding: DirectiveBinding<MouseWheelHandlers>) {
    if (!binding.value) return;

    const { onZoom, onScroll } = binding.value;

    const handleWheel = (event: WheelEvent) => {
      const { ctrlKey, metaKey, deltaY } = event;

      // Handle zooming with Ctrl/Cmd + wheel
      if (ctrlKey || metaKey) {
        event.preventDefault();

        if (typeof onZoom === 'function') {
          // Get mouse position relative to the element for focal point zooming
          const rect = el.getBoundingClientRect();
          const mouseX = event.clientX - rect.left;
          const mouseY = event.clientY - rect.top;

          onZoom({
            delta: deltaY,
            direction: deltaY > 0 ? 'out' : 'in',
            event,
            mouseX,
            mouseY
          });
        }
      }
      // Handle scrolling
      else if (typeof onScroll === 'function') {
        // Allow default browser scrolling but capture scroll position
        onScroll({
          deltaX: event.deltaX,
          deltaY: event.deltaY,
          event
        });
      }
    };

    // Add the event listener
    el.addEventListener('wheel', handleWheel, { passive: false });

    // Store the handler on the element for cleanup
    el._wheelHandler = handleWheel;
  },

  beforeUnmount(el: MouseWheelElement) {
    // Clean up the event listener when component is unmounted
    if (el._wheelHandler) {
      el.removeEventListener('wheel', el._wheelHandler);
      delete el._wheelHandler;
    }
  }
};

/**
 * Helper functions for document format management
 */
export const FormatManager = {
  /**
   * Calculate the appropriate dimensions for a document type
   */
  calculateDimensions(formatType: string, aspectRatio: string): FormatDimension {
    // Presentation formats
    if (formatType === 'presentation') {
      if (aspectRatio === 'Presentation (16:9)' || aspectRatio === '16:9') {
        return { width: 960, height: 540 };
      } else if (aspectRatio === 'Presentation (4:3)' || aspectRatio === '4:3') {
        return { width: 960, height: 720 };
      }
    }

    // Document formats
    else if (formatType === 'document') {
      if (aspectRatio === 'A4') {
        return { width: 794, height: 1123 }; // ~210mm × 297mm at 96dpi
      } else if (aspectRatio === 'A5') {
        return { width: 559, height: 794 };  // ~148mm × 210mm at 96dpi
      } else if (aspectRatio === 'A6') {
        return { width: 397, height: 559 };  // ~105mm × 148mm at 96dpi
      } else if (aspectRatio === 'Letter') {
        return { width: 816, height: 1056 }; // 8.5in × 11in at 96dpi
      }
    }

    // Default to 16:9 presentation if format not recognized
    return { width: 960, height: 540 };
  },

  /**
   * Determine if a given format is a document (as opposed to presentation)
   */
  isDocumentFormat(formatType: string): boolean {
    return formatType === 'document';
  },

  /**
   * Get optimal zoom level for a document format to fit the container
   */
  getOptimalZoom(
    formatType: string,
    aspectRatio: string,
    containerWidth: number,
    containerHeight: number,
    padding: number = 40
  ): number {
    const { width, height } = this.calculateDimensions(formatType, aspectRatio);

    // Calculate available space
    const availableWidth = containerWidth - padding * 2;
    const availableHeight = containerHeight - padding * 2;

    // Calculate zoom percentages for width and height constraints
    const widthZoom = (availableWidth / width) * 100;
    const heightZoom = (availableHeight / height) * 100;

    // Use the smaller zoom level to ensure the document fits
    let optimalZoom = Math.min(widthZoom, heightZoom);

    // Round to nearest 5%
    optimalZoom = Math.floor(optimalZoom / 5) * 5;

    // Constrain to reasonable bounds (20% to 150%)
    return Math.max(20, Math.min(150, optimalZoom));
  },

  /**
   * Calculate scroll position to maintain zoom focus point
   */
  calculateZoomScrollPosition(
    currentZoom: number,
    newZoom: number,
    mouseX: number,
    mouseY: number,
    canvasWidth: number,
    canvasHeight: number,
    scrollLeft: number,
    scrollTop: number
  ): { scrollLeft: number; scrollTop: number } {
    // Calculate the point on the canvas that the mouse is over
    const pointXBeforeZoom = (scrollLeft + mouseX) / currentZoom;
    const pointYBeforeZoom = (scrollTop + mouseY) / currentZoom;

    // Calculate where that same point would be after zooming
    const pointXAfterZoom = pointXBeforeZoom * newZoom;
    const pointYAfterZoom = pointYBeforeZoom * newZoom;

    // Calculate new scroll position to keep the point under the mouse
    const newScrollLeft = pointXAfterZoom - mouseX;
    const newScrollTop = pointYAfterZoom - mouseY;

    return {
      scrollLeft: Math.max(0, newScrollLeft),
      scrollTop: Math.max(0, newScrollTop)
    };
  },

  /**
   * Calculate centering offsets to position canvas in viewport
   */
  calculateCenteringOffsets(
    canvasWidth: number,
    canvasHeight: number,
    viewportWidth: number,
    viewportHeight: number,
    zoom: number = 100
  ): { offsetX: number; offsetY: number } {
    const zoomFactor = zoom / 100;
    const scaledWidth = canvasWidth * zoomFactor;
    const scaledHeight = canvasHeight * zoomFactor;

    // If canvas is smaller than viewport, center it
    let offsetX = 0;
    let offsetY = 0;

    if (scaledWidth < viewportWidth) {
      offsetX = (viewportWidth - scaledWidth) / 2;
    }

    if (scaledHeight < viewportHeight) {
      offsetY = (viewportHeight - scaledHeight) / 2;
    }

    return { offsetX, offsetY };
  }
};

/**
 * Utility function to convert slide items between different formats
 */
export const convertItemsToNewFormat = (
  items: any[],
  sourceFormat: { type: string; aspectRatio: string },
  targetFormat: { type: string; aspectRatio: string }
): any[] => {
  if (!items || !Array.isArray(items) || items.length === 0) return [];

  // Get dimensions for source and target
  const sourceSize = FormatManager.calculateDimensions(sourceFormat.type, sourceFormat.aspectRatio);
  const targetSize = FormatManager.calculateDimensions(targetFormat.type, targetFormat.aspectRatio);

  // Calculate scaling factors
  const scaleX = targetSize.width / sourceSize.width;
  const scaleY = targetSize.height / sourceSize.height;

  // Copy items and adjust their positions and sizes
  return items.map(item => {
    const newItem = { ...item };

    // Scale position
    newItem.x = Math.round(item.x * scaleX);
    newItem.y = Math.round(item.y * scaleY);

    // Scale size
    newItem.width = Math.round(item.width * scaleX);
    newItem.height = Math.round(item.height * scaleY);

    return newItem;
  });
};